/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { PreviewSuggestionButton } from './PreviewSuggestionButton.tsx';
import { usePreviewSuggestion } from './usePreviewSuggestion.ts';

const styles = cssMap({
	// Standalone inline-comment popup: nothing else pads the popup body at this level.
	popup: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
	},
	// Comments panel: the thread row already provides the horizontal padding.
	panel: {
		marginTop: token('space.150'),
	},
});

type PreviewSuggestionFooterProps = {
	/**
	 * Marker ref (annotation id) of the comment thread this footer belongs to. The footer renders
	 * the button only for a suggestion generated from that thread. Pass `undefined` for surfaces
	 * without a marker ref (e.g. a general comment) — nothing will render.
	 */
	markerRef?: string;
	/** Called just before the suggestion is selected, e.g. to close the popup or switch tab. */
	onPreview?: () => void;
	/** Which surface this is rendered on; picks the spacing that suits it. */
	spacing: 'popup' | 'panel';
	testId?: string;
};

/**
 * Footer for an inline-comment thread that offers to preview the AI suggestion generated from
 * that thread. Renders nothing when the thread has no suggestion.
 *
 * Mount this only under `platform_editor_ai_content_suggested_edits` — the experiment is checked
 * at the call sites so this component's shared-plugin-state subscription is never set up when
 * suggested edits are off.
 */
export function PreviewSuggestionFooter(props: PreviewSuggestionFooterProps): JSX.Element | null {
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();
	if (!isAdminHubAIEnabled || !isAIEarlyAccessEnabled) {
		return null;
	}

	return <EligiblePreviewSuggestionFooter {...props} />;
}

function EligiblePreviewSuggestionFooter({
	markerRef,
	onPreview,
	spacing,
	testId,
}: PreviewSuggestionFooterProps): JSX.Element | null {
	const { editorApi, suggestionId } = usePreviewSuggestion(markerRef);

	if (!suggestionId) {
		return null;
	}

	return (
		<div css={styles[spacing]}>
			<PreviewSuggestionButton
				editorApi={editorApi}
				suggestionId={suggestionId}
				onPreview={onPreview}
				testId={testId}
			/>
		</div>
	);
}
